import { useSelector } from 'react-redux';
import type { RootState } from '../store/store';
import { MCGRAW_HILL_HANDBOOK_EXAM_ID, useIsMcGrawHill } from './mcgrawHill';
import { examsSelectors } from '../slices/exams/examsSlice';
import { isPreclinicalExam } from '../utils/examUtils';
import { paths } from '../routes/paths';
import { useMcGrawHillVertical } from './mcgrawHillVerticalScope';

/**
 * mcgraw-hill (ORA-1261, ORA-1268): the Nursing vertical's courses. In
 * McGraw Hill's view, Nursing has a Courses nav item and page with DUT's
 * Nursing Pharmacology (the handbook exam), and its exam menu lists NCLEX-RN
 * and a Courses group.
 */

/** The Courses page (not `/courses`: a single segment is a cohort link). */
export const DUT_COURSES_PATH = '/dut/courses';

/** Whether McGraw Hill is showing with the Nursing vertical picked. */
export const useHasNursingCourses = (): boolean => {
  const isMcGrawHill = useIsMcGrawHill();
  const vertical = useMcGrawHillVertical();
  return isMcGrawHill && vertical === 'nursing';
};

/** Preclinical primary with the DUT handbook as the active custom exam. */
export const selectIsHandbookActive = (state: RootState): boolean =>
  isPreclinicalExam(examsSelectors.selectPrimaryExam(state)) &&
  examsSelectors.selectActiveCustomExam(state)?.id ===
    MCGRAW_HILL_HANDBOOK_EXAM_ID;

/**
 * Whether the nav's Courses item is the active one: on the Courses page, and
 * on the Dashboard while the course (the handbook exam) is the active exam.
 */
export const useIsDutCoursesNavActive = (pathname: string): boolean => {
  const hasCourses = useHasNursingCourses();

  const isHandbookActive = useSelector(
    (state: Partial<RootState>) =>
      Boolean(state.exams) && selectIsHandbookActive(state as RootState)
  );

  if (!hasCourses) return false;
  if (pathname === DUT_COURSES_PATH) return true;
  return pathname === paths.dashboard && isHandbookActive;
};
